import React, { useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Accordion, AccordionTab } from 'primereact/accordion';
import globalUserPage from '../../../../../stores/global-user-page';
import { observer } from 'mobx-react-lite';
import { formatDate } from '../../../../../utils/date-format';
import { HeaderCards } from './components/header-cards';
import { InstallmentsTable } from './components/installments-table';
import {
  InstallmentBankTransferWithPlanItemsDetails,
  PaymentInstallmentBankTransferEntity,
} from '../../../../../api/types/students/user-details-student';
import { PaymentInstallmentStatus } from '../../../../../api/types/payments/installments/entities';
import { UserSummaryType } from '../../../../../api/types/user/user-summary.type';

export interface UserPageStudentInstallmentsBankTransferDatamodel {
  user: UserSummaryType;
}

export const UserPageStudentInstallmentsBankTransfer: React.FC<UserPageStudentInstallmentsBankTransferDatamodel> =
  observer(({ user }) => {
    const [activeIndex, setActiveIndex] = useState<number | number[] | null>(
      null,
    );

    const installments = useMemo(() => {
      return globalUserPage.studentDetails?.installmentsBankTransfer ?? [];
    }, [globalUserPage.studentDetails?.installmentsBankTransfer]);

    // Flatten all installments from all plans for summary calculation
    const allInstallmentItems = useMemo(() => {
      return installments.flatMap((installmentPlan) =>
        installmentPlan.installment.map((item) => ({
          ...item,
          planTotalAmount: installmentPlan.installmentPlan.totalAmount,
          planNumberOfInstallments:
            installmentPlan.installmentPlan.numberOfInstallments,
          planFirstPaymentDate:
            installmentPlan.installmentPlan.firstPaymentDate,
          planPaymentDayOfMonth:
            installmentPlan.installmentPlan.paymentDayOfMonth,
          planStatus: installmentPlan.installmentPlan.status,
        })),
      );
    }, [installments]);

    // Calculate summary statistics
    const summary = useMemo(() => {
      const totalPlans = installments.length;
      const totalAmount = installments.reduce(
        (sum, plan) => sum + plan.installmentPlan.totalAmount,
        0,
      );
      const paidAmount = allInstallmentItems
        .filter((item) => item.status === PaymentInstallmentStatus.PAID)
        .reduce((sum, item) => sum + item.amount, 0);
      const pendingAmount = allInstallmentItems
        .filter((item) => item.status === PaymentInstallmentStatus.PENDING)
        .reduce((sum, item) => sum + item.amount, 0);
      const paidCount = allInstallmentItems.filter(
        (item) => item.status === PaymentInstallmentStatus.PAID,
      ).length;
      const totalInstallments = allInstallmentItems.length;

      return {
        totalPlans,
        totalAmount,
        paidAmount,
        pendingAmount,
        paidCount,
        totalInstallments,
      };
    }, [installments, allInstallmentItems]);

    // Calculate stats for each plan
    const getPlanStats = (
      plan: InstallmentBankTransferWithPlanItemsDetails,
    ) => {
      const paidCount = plan.installment.filter(
        (item) => item.status === PaymentInstallmentStatus.PAID,
      ).length;
      const failedCount = plan.installment.filter(
        (item) => item.status === PaymentInstallmentStatus.FAILED,
      ).length;
      const paymentServicesCount =
        plan.paymentInstallmentPlanItemAllocations.length;

      return {
        paidCount,
        failedCount,
        paymentServicesCount,
      };
    };

    // Render the header for each accordion tab
    const renderAccordionHeader = (
      plan: InstallmentBankTransferWithPlanItemsDetails,
    ) => {
      const stats = getPlanStats(plan);
      const createdAt = plan.installmentPlan.createdAt;

      return (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '32px',
            alignItems: 'center',
            width: '100%',
            padding: '12px 0',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '16px', color: '#6b7280' }}>
              נוצר בתאריך
            </span>
            <span
              style={{ fontWeight: 'bold', color: '#1f2937', fontSize: '18px' }}
            >
              {formatDate(createdAt)}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '16px', color: '#6b7280' }}>
              סכום כולל
            </span>
            <span
              style={{ fontWeight: 'bold', color: '#1f2937', fontSize: '18px' }}
            >
              ₪{plan.installmentPlan.totalAmount.toLocaleString()}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '16px', color: '#6b7280' }}>
              מספר תשלומים
            </span>
            <span
              style={{ fontWeight: 'bold', color: '#1f2937', fontSize: '18px' }}
            >
              {plan.installmentPlan.numberOfInstallments}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '16px', color: '#6b7280' }}>
              שולמו בהצלחה
            </span>
            <span
              style={{ fontWeight: 'bold', color: '#22c55e', fontSize: '18px' }}
            >
              {stats.paidCount}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '16px', color: '#6b7280' }}>נכשלו</span>
            <span
              style={{
                fontWeight: 'bold',
                color: stats.failedCount > 0 ? '#ef4444' : '#6b7280',
                fontSize: '18px',
              }}
            >
              {stats.failedCount}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '16px', color: '#6b7280' }}>
              מספר שירותי תשלום
            </span>
            <span
              style={{ fontWeight: 'bold', color: '#1f2937', fontSize: '18px' }}
            >
              {stats.paymentServicesCount}
            </span>
          </div>
        </div>
      );
    };

    if (installments.length === 0) {
      return (
        <div style={{ width: '100%', padding: '24px' }}>
          <Card>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '48px',
                gap: '16px',
              }}
            >
              <i
                className="pi pi-building"
                style={{ fontSize: '48px', color: '#9ca3af' }}
              />
              <h3 style={{ margin: 0, color: '#4b5563' }}>
                אין הוראות קבע בהעברה בנקאית
              </h3>
              <p style={{ margin: 0, color: '#6b7280', textAlign: 'center' }}>
                לא נמצאו הוראות קבע בהעברה בנקאית עבור משתמש זה.
              </p>
            </div>
          </Card>
        </div>
      );
    }

    return (
      <div style={{ width: '100%', padding: '16px' }}>
        {/* Summary Cards */}
        <HeaderCards summary={summary} />

        {/* Installment Plans Accordion */}
        <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#1f2937' }}>
          הוראות קבע בהעברה בנקאית
        </h3>
        <Accordion
          activeIndex={activeIndex}
          onTabChange={(e) => setActiveIndex(e.index)}
          multiple
        >
          {installments.map((plan, index) => (
            <AccordionTab
              key={plan.installmentPlan.id}
              header={renderAccordionHeader(plan)}
              headerStyle={{
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                marginBottom: '8px',
              }}
              contentStyle={{
                padding: '8px',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '0 0 8px 8px',
              }}
            >
              <InstallmentsTable user={user} plan={plan} />
            </AccordionTab>
          ))}
        </Accordion>

        <style>{`
        .p-accordion .p-accordion-header .p-accordion-header-link {
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }
        
        .p-accordion .p-accordion-header:not(.p-disabled).p-highlight .p-accordion-header-link {
          background-color: #f3f4f6;
          border-color: #d1d5db;
        }
        
        .p-accordion .p-accordion-header:not(.p-disabled) .p-accordion-header-link:hover {
          background-color: #f3f4f6;
        }
        
        .p-accordion .p-accordion-content {
          border: none;
        }
      `}</style>
      </div>
    );
  });
